import _ from 'lodash';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {
  isEventInRange,
  isUnsavedDraftCalendarEventVersion,
} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {AppAction} from '../../appActions';
import {DataCascade} from '../../data/dataCascade';
import {DataGroupsActionsEnum} from '../../data/dataGroupsActions';
import {CalendarsPanelActionsEnum} from './calendarsPanelActions';
import {CalendarsPanelState} from './calendarsPanelState';
import {findParametersRange} from './calendarsParametersHelpers';
import {calendarsPanelSchedulingReducer} from './scheduling/calendarsPanelSchedulingReducer';

/*
 * Calendar panel.
 */

export function calendarsPanelReducer(
  state: CalendarsPanelState,
  action: AppAction,
  dataCascade: DataCascade,
): CalendarsPanelState {
  switch (action.type) {
    case CalendarsPanelActionsEnum.NAVIGATE_CALENDARS:
      return {
        ...state,
        scheduling: calendarsPanelSchedulingReducer(state.scheduling, action),
        status: AsyncStatusesEnum.LOADING,
        parameters: action.payload.parameters,
        cancelTokenId: action.payload.cancelTokenId,
        highlightedCalendarEventParameters: undefined,
      };

    case CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_COMPLETED:
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        calendarEventVersionUids: action.payload.calendarEventVersionUids,
        maxUpdatedAt: action.payload.maxUpdatedAt,
      };

    case CalendarsPanelActionsEnum.FETCH_CALENDAR_EVENT_VERSIONS_FAILED:
      return {
        ...state,
        status: AsyncStatusesEnum.FAILED,
      };

    case CalendarsPanelActionsEnum.HIGHLIGHT_CALENDAR_EVENT: {
      const {eventParameters} = action.payload;
      const {focusedCalendarEventParameters} = state;
      return {
        ...state,
        highlightedCalendarEventParameters: eventParameters,
        focusedCalendarEventParameters: eventParameters ? undefined : focusedCalendarEventParameters,
      };
    }

    case CalendarsPanelActionsEnum.FOCUS_CALENDAR_EVENT:
      return {
        ...state,
        highlightedCalendarEventParameters: undefined,
        focusedCalendarEventParameters: action.payload.eventParameters,
        popoverCalendarEventParameters: undefined,
      };

    case CalendarsPanelActionsEnum.POP_CALENDAR_EVENT:
      return {
        ...state,
        popoverCalendarEventParameters: action.payload.eventParameters,
      };

    case CalendarsPanelActionsEnum.UPDATE_VISIBLE_CALENDARS:
      return {
        ...state,
        scheduling: calendarsPanelSchedulingReducer(state.scheduling, action),
        viewingNowCalendars: action.payload.viewingNowCalendars,
      };

    case CalendarsPanelActionsEnum.ADD_CALENDAR_ADDRESS:
      return {
        ...state,
        scheduling: calendarsPanelSchedulingReducer(state.scheduling, action),
      };

    default: {
      // Only for consistency with other reducers that use dataCascade.updatedResources. Calendar panel actions don't
      // currently dispatch DATA_UPDATED actions with calendar event versions.
      if (action.type === DataGroupsActionsEnum.DATA_UPDATED && action.payload.shouldNotCascade) {
        return state;
      }

      // Handle scheduling related actions.
      const newSchedulingState = calendarsPanelSchedulingReducer(state.scheduling, action);
      if (newSchedulingState !== state.scheduling) {
        return {
          ...state,
          scheduling: newSchedulingState,
        };
      }

      // Determine if we have any events in the saving state that need to be displayed in the current calendar view.
      // TODO(paul): Handle recurring events in range when we support recurring event creation.
      const {calendarEventVersions = []} = dataCascade.updatedResources;
      const {startsAt, endsAt} = findParametersRange(state.parameters);
      const calendarEventVersionsInView = _(calendarEventVersions)
        .filter((v) => isEventInRange(v, startsAt, endsAt) && !isUnsavedDraftCalendarEventVersion(v))
        .value();

      const shouldBeAdded = _(calendarEventVersionsInView)
        .map((v) => v.uid)
        .difference(state.calendarEventVersionUids)
        .value();

      if (shouldBeAdded.length > 0) {
        return {
          ...state,
          calendarEventVersionUids: state.calendarEventVersionUids.concat(shouldBeAdded),
        };
      }

      return state;
    }
  }
}
